/**
 * NdsEditorProvider — schema persistence only (post-floating-windows refactor).
 *
 * Popup/window management has moved to `useFloatingWindows`. This provider
 * now only exposes:
 *   • saveItemPatch({kind, verticalId, itemId, patch})  → PUT master schema
 *   • openTileEditor(target) / fireTileAction(cfg) helpers that dispatch
 *     into the floating windows manager (kept here so tile components have
 *     one import).
 *
 * The old `editMode` toggle is DELETED. Editing is always available via
 * right-click / long-press on any tile.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { useNexOs } from "@/lib/nexOs";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { useDemoMode } from "@/lib/demoMode";

const Ctx = createContext(null);

const cloneSchema = (s) => JSON.parse(JSON.stringify(s));

const findVerticalIdx = (schema, verticalId) =>
  (schema.verticals || []).findIndex((v) => v.id === verticalId);

const applyPatch = (schema, kind, verticalId, itemId, patch, actorEmail) => {
  const next = cloneSchema(schema);
  const vi = findVerticalIdx(next, verticalId);
  if (vi < 0) return null;

  const audit = (item) => {
    item.audit = Array.isArray(item.audit) ? item.audit : [];
    item.audit.unshift({
      ts:  new Date().toISOString(),
      who: actorEmail || "unknown",
      changes: Object.keys(patch),
    });
    if (item.audit.length > 50) item.audit = item.audit.slice(0, 50);
  };

  if (kind === "kpi") {
    const arr = next.verticals[vi].kpis || [];
    const idx = arr.findIndex((k) => k.id === itemId);
    if (idx < 0) return null;
    Object.assign(arr[idx], patch);
    audit(arr[idx]);
  } else if (kind === "step") {
    const arr = next.verticals[vi].steps || [];
    const idx = arr.findIndex((s) => s.id === itemId);
    if (idx < 0) return null;
    Object.assign(arr[idx], patch);
    audit(arr[idx]);
  } else if (kind === "capability") {
    const tpl = next.capabilities_template || [];
    const idx = tpl.findIndex((c) => c.id === itemId);
    if (idx < 0) return null;
    Object.assign(tpl[idx], patch);
    audit(tpl[idx]);
    for (const v of next.verticals || []) {
      if (Array.isArray(v.capabilities)) {
        const j = v.capabilities.findIndex((c) => c.id === itemId);
        if (j >= 0) Object.assign(v.capabilities[j], patch);
      }
    }
  } else {
    return null;
  }
  return next;
};

export const NdsEditorProvider = ({ children }) => {
  const { schema, reloadSchema, reloadLive, vertical, mode } = useNexOs();
  const { openWindow } = useFloatingWindows();
  const demoMode = useDemoMode();
  const meRef = useRef(null);
  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("nex_user") || "null");
      if (u?.email) meRef.current = u.email;
    } catch {}
  }, []);

  /**
   * Open the Quick-Edit floating window for a tile.
   *   target = { kind: "kpi"|"step"|"capability", verticalId, itemId }
   */
  const openTileEditor = useCallback((target) => {
    if (!target) return;
    // In demoMode we now DO open the editor so prospects can preview the
    // right-click/pencil workflow. `saveItemPatch` short-circuits the PUT
    // below so nothing persists — the toast says as much.
    openWindow("quickEdit", target);
  }, [openWindow]);

  /**
   * Open a functional action window (Brief Intake, New Contact, Send SMS, etc.)
   *   cfg = { kind, title?, description?, href? }
   */
  const fireTileAction = useCallback((cfg) => {
    if (!cfg?.kind) return;
    openWindow(cfg.kind, cfg);
  }, [openWindow]);

  const saveItemPatch = useCallback(async ({ kind, verticalId, itemId, patch }) => {
    if (!schema) return false;
    const next = applyPatch(schema, kind, verticalId, itemId, patch, meRef.current);
    if (!next) {
      toast.error("Could not locate item to update");
      return false;
    }
    // Demo mode: fake a successful save so prospects can rename tiles and
    // see the "Saved" toast without hitting the schema PUT (which requires
    // admin auth anyway).  Nothing persists on the server.
    if (demoMode) {
      toast.success("Demo · Rename would persist in your live console");
      return true;
    }
    setSaving(true);
    try {
      await apiClient.put("/admin/nex-os/schema", { schema: next });
      await reloadSchema();
      await reloadLive(vertical, mode);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(false); }
  }, [schema, reloadSchema, reloadLive, vertical, mode, demoMode]);

  const value = useMemo(() => ({
    openTileEditor, fireTileAction,
    saveItemPatch, saving,
  }), [openTileEditor, fireTileAction, saveItemPatch, saving]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useNdsEditor = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useNdsEditor must be used inside <NdsEditorProvider>");
  return v;
};

/** Field schema per item kind — drives which inputs render in Quick Edit. */
export const FIELD_SCHEMAS = {
  kpi: [
    { key: "label",  label: "Label",          type: "text" },
    { key: "value",  label: "Live Value",     type: "number" },
    { key: "format", label: "Format",         type: "select", options: ["currency", "int", "percent"] },
    { key: "icon",   label: "Icon",           type: "select", options: ["dollar", "briefcase", "users", "receipt"] },
    { key: "notes",  label: "Notes",          type: "textarea" },
  ],
  step: [
    { key: "title",        label: "Title",         type: "text" },
    { key: "status_badge", label: "Status Badge",  type: "text" },
    { key: "score",        label: "Score",         type: "number" },
    { key: "coaching",     label: "Coaching Text", type: "textarea" },
    { key: "notes",        label: "Notes",         type: "textarea" },
  ],
  capability: [
    { key: "label",  label: "Label",       type: "text" },
    { key: "status", label: "Status Text", type: "text" },
    { key: "tone",   label: "Tone",        type: "select", options: ["live", "wip", "queued", "dim"] },
    { key: "href",   label: "Deep Link",   type: "text" },
    { key: "notes",  label: "Notes",       type: "textarea" },
  ],
};

/**
 * useTileHandlers — one hook every editable tile uses to bind:
 *   • right-click (contextmenu) / long-press → open Quick-Edit window
 *   • left click behaviour is up to the caller (usually: open action window)
 *
 * Returns spread-props { onContextMenu, onTouchStart, onTouchEnd, onTouchMove }.
 */
export const useTileHandlers = ({ target, onEdit }) => {
  const timer = useRef(null);
  const moved = useRef(false);

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

  return {
    onContextMenu: (e) => {
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      onEdit?.(target);
    },
    onTouchStart: (e) => {
      moved.current = false;
      clearTimer();
      timer.current = setTimeout(() => {
        if (!moved.current && target) onEdit?.(target);
      }, 550);
    },
    onTouchMove: () => { moved.current = true; clearTimer(); },
    onTouchEnd:  () => { clearTimer(); },
    onTouchCancel: () => { clearTimer(); },
  };
};
